import Tags from "components/black/Tags";
import PostReactions from "components/reactions/PostReactions";
import RelativeTime from "components/ui/RelativeTime";
import classNames from "components/ui/classNames";
import LinkButton from "components/ui/LinkButton";
import { LockIcon, PinIcon } from "lucide-react";
import Link from "next/link";
import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { SimpleTextTooltip } from "components/ui/Tooltip";
import type { IAssetDiscussionCommentFieldsFragment } from "../../lib/graphql/fragments/__generated__/AssetDiscussionCommentFields.generated";
import type { IAssetDiscussionCommentParticipantsFieldsFragment } from "../../lib/graphql/fragments/__generated__/AssetDiscussionCommentParticipantsFields.generated";
import type { IAssetDiscussionCommentRepliesPaginationFieldsFragment } from "../../lib/graphql/fragments/__generated__/AssetDiscussionCommentRepliesPaginationFields.generated";
import useSelfie from "../../lib/hooks/useSelfie";
import translateTypenameIntoAssetType from "../../lib/utils/translateTypenameIntoAssetType";
import { IReactionSubject } from "../../types/graphqlTypes";
import Avatar from "../core/Avatar";
import { Menu, MenuActionButtonItem, MenuDivider, MenuTitle } from "../core/menu";
import ActionsDropdown from "./ActionsDropdown";
import AssetDiscussionCommentParticipants from "./AssetDiscussionCommentParticipants";
import AssetDiscussionCommentReply from "./AssetDiscussionCommentReply";
import CommentContent from "./CommentContent";
import HorizontalLineDivider from "./HorizontalLineDivider";
import InlineAssetDiscussionCommentError from "./InlineAssetDiscussionCommentError";
import ViewMoreReplies from "./ViewMoreReplies";
import IndividualCommentInputForm from "./form/IndividualCommentInputForm";
import ReplyCommentInputForm from "./form/ReplyCommentInputForm";
import translateServerErrorIntoFriendlyMessage from "./form/translateServerErrorIntoFriendlyMessage";
import useCommentActions from "./hooks/useCommentActions";
import ReplyToAssetDiscussionCommentMutation from "./mutations/ReplyToAssetDiscussionCommentMutation";

interface IAssetDiscussionCommentProps {
  comment: IAssetDiscussionCommentFieldsFragment;
  participants?: IAssetDiscussionCommentParticipantsFieldsFragment;
  replies?: IAssetDiscussionCommentRepliesPaginationFieldsFragment;
  allowToReplyRootComment?: boolean;
  allowToReplyIndividualReplies?: boolean;
  shouldDisplayReplies?: boolean;
}

export default function AssetDiscussionComment({
  comment,
  participants,
  replies,
  allowToReplyRootComment = true,
  allowToReplyIndividualReplies = true,
  shouldDisplayReplies = false,
}: IAssetDiscussionCommentProps) {
  const selfie = useSelfie();
  const intl = useIntl();

  const [isEditingContent, setIsEditingContent] = useState<boolean>(false);
  const [isWritingReply, setIsWritingReply] = useState<boolean>(false);
  const [isShowingReplies, setIsShowingReplies] = useState<boolean>(shouldDisplayReplies);

  const {
    destroyComment,
    editComment,
    lockComment,
    unlockComment,
    pinComment,
    unpinComment,

    isDestroyingComment,
    isLockingComment,
    isUnlockingComment,
    isPinningComment,
    isUnpinningComment,

    destroyCommentError,
    editCommentError,
    lockCommentError,
    unlockCommentError,
    pinCommentError,
    unpinCommentError,
  } = useCommentActions(comment);

  const viewerCanTakeActions = comment.viewerCanEdit || comment.viewerCanDestroy || (selfie && selfie.isSiteAdmin);

  return (
    <div className="box-border grid grid-cols-[min-content_1fr] gap-4 font-aeonik font-normal">
      <div className="relative">
        <Avatar
          src={comment.author.pictureUrl}
          hashValue={comment.author._id}
          alt={comment.author.fullName}
          squircle={true}
          variant="rounded"
          size={42}
        />
      </div>

      <div className="flex flex-col space-y-4">
        {comment.isLocked && (
          <div className={classNames("flex items-center gap-x-1 font-medium text-red-600 dark:text-red-300")}>
            <LockIcon className="size-4" absoluteStrokeWidth={true} />
            <span className="text-sm tracking-wider">
              <FormattedMessage defaultMessage="Comentário trancado" id="Kf1Xnh" />
            </span>
          </div>
        )}

        {comment.isPinned && comment.pinnedBy && (
          <div className={classNames("flex items-center gap-x-1 font-medium text-stone-600 dark:text-stone-300")}>
            <PinIcon className="size-4" absoluteStrokeWidth={true} />
            <span className="text-sm tracking-wider">
              <FormattedMessage defaultMessage="Comentário fixado" id="i6qyUs" />
            </span>
          </div>
        )}

        <div className="mt-0 flex flex-col">
          <div className="flex items-center space-x-2">
            <strong className="text-sm dark:text-slate-200">{comment.author.fullName}</strong>
            <Tags viewer={comment.author} />
          </div>

          <div>
            <Link
              href="/comments/[shortId]"
              as={`/comments/${comment.shortId}`}
              passHref={true}
              className="inline-flex hover:underline"
            >
              <RelativeTime
                className="text-xs font-medium text-slate-500 dark:text-gray-500 md:text-sm"
                timeStamp={comment.postedAt}
              />
            </Link>

            {comment.lastEditedAt && (
              <span className="text-xs font-medium text-slate-500 md:text-sm">{" • editado"}</span>
            )}
          </div>
        </div>
      </div>
      <div className="col-span-full w-full space-y-4 lg:col-start-2">
        {isEditingContent ? (
          <>
            <IndividualCommentInputForm
              submitButtonText="Editar"
              onSubmit={async (bodyAsPlainText) => {
                await editComment(bodyAsPlainText);
                setIsEditingContent(false);
              }}
              onCancel={() => setIsEditingContent(false)}
              bodyAsPlainText={comment.bodyAsPlainText}
            />

            <HorizontalLineDivider />
          </>
        ) : (
          <CommentContent bodyAsPlainText={comment.bodyAsPlainText} />
        )}

        <PostReactions
          subjectId={comment._id}
          subjectType={IReactionSubject.AssetDiscussionComment}
          reactable={comment}
        />

        <div className="grid grid-cols-2">
          <div className="grid auto-cols-max grid-flow-col gap-x-3">
            {(selfie?.isPremium || selfie?.isTrialing) &&
              allowToReplyRootComment &&
              replies &&
              replies.totalCount > 0 && (
                <LinkButton
                  variant="primary"
                  onClick={() => {
                    if (isShowingReplies && isWritingReply && comment.viewerCanReply) {
                      setIsWritingReply((previous) => !previous);
                    }

                    setIsShowingReplies((previous) => !previous);
                  }}
                >
                  {!isShowingReplies ? `Ver respostas (${replies.totalCount})` : "Ocultar respostas"}
                </LinkButton>
              )}

            {comment.viewerCanReply &&
              allowToReplyRootComment &&
              ((replies && replies.totalCount === 0) || isShowingReplies) && (
                <LinkButton
                  variant={isWritingReply ? "danger" : "secondary"}
                  onClick={() => {
                    setIsWritingReply(!isWritingReply);
                  }}
                >
                  {isWritingReply ? (
                    <FormattedMessage defaultMessage="Cancelar" id="nZLeaQ" />
                  ) : (
                    <FormattedMessage defaultMessage="Responder" id="e/WuSx" />
                  )}
                </LinkButton>
              )}

            {!selfie?.isPremium && !selfie?.isTrialing && replies && replies.totalCount > 0 && (
              <SimpleTextTooltip content="Você precisa ser Premium para ver as respostas">
                <span className="cursor-default select-none text-sm font-medium text-blue-600">
                  Respostas ({replies.totalCount})
                </span>
              </SimpleTextTooltip>
            )}

            {selfie?.isPremium && replies && replies.totalCount > 0 && !allowToReplyRootComment && (
              <span className="cursor-default text-sm font-medium text-blue-600">Respostas ({replies.totalCount})</span>
            )}

            {viewerCanTakeActions && (
              <ActionsDropdown>
                {({ closeOnClick }) => (
                  <Menu>
                    {comment.viewerCanEdit && (
                      <MenuActionButtonItem
                        variant="primary"
                        onClick={closeOnClick(() => setIsEditingContent(!isEditingContent))}
                      >
                        Editar
                      </MenuActionButtonItem>
                    )}

                    {selfie && selfie.isSiteAdmin && (
                      <>
                        <MenuActionButtonItem
                          variant="primary"
                          onClick={closeOnClick(() => {
                            navigator.clipboard.writeText(comment.author.email);
                          })}
                        >
                          Copiar email
                        </MenuActionButtonItem>

                        <MenuActionButtonItem
                          variant="primary"
                          onClick={closeOnClick(() => {
                            navigator.clipboard.writeText(comment.author._id);
                          })}
                        >
                          Copiar ID
                        </MenuActionButtonItem>

                        <MenuDivider />

                        <MenuTitle>Moderação</MenuTitle>

                        {comment.isPinned ? (
                          <MenuActionButtonItem disabled={isUnpinningComment} onClick={closeOnClick(unpinComment)}>
                            {isUnpinningComment ? "Desfixando..." : "Desfixar"}
                          </MenuActionButtonItem>
                        ) : (
                          <MenuActionButtonItem disabled={isPinningComment} onClick={closeOnClick(pinComment)}>
                            {isPinningComment ? "Fixando..." : "Fixar"}
                          </MenuActionButtonItem>
                        )}

                        {comment.isLocked ? (
                          <MenuActionButtonItem disabled={isUnlockingComment} onClick={closeOnClick(unlockComment)}>
                            {isUnlockingComment ? "Destrancando..." : "Destrancar"}
                          </MenuActionButtonItem>
                        ) : (
                          <MenuActionButtonItem disabled={isLockingComment} onClick={closeOnClick(lockComment)}>
                            {isLockingComment ? "Trancando..." : "Trancar"}
                          </MenuActionButtonItem>
                        )}
                      </>
                    )}

                    <MenuDivider />

                    {comment.viewerCanDestroy && (
                      <MenuActionButtonItem
                        variant="danger"
                        disabled={isDestroyingComment}
                        onClick={() => {
                          // eslint-disable-next-line no-alert, no-restricted-globals
                          const didConfirm = confirm(
                            intl.formatMessage({
                              defaultMessage: "Tem certeza que deseja apagar o comentário?",
                              id: "UkuPe0",
                            }),
                          );

                          if (didConfirm) {
                            destroyComment();
                          }
                        }}
                      >
                        {isDestroyingComment ? "Apagando..." : "Apagar"}
                      </MenuActionButtonItem>
                    )}
                  </Menu>
                )}
              </ActionsDropdown>
            )}
            {participants && participants.nodes.length > 0 && (
              <AssetDiscussionCommentParticipants
                participants={participants.nodes}
                totalCount={participants.totalCount}
              />
            )}
          </div>
        </div>

        {selfie && isWritingReply && (
          <>
            <HorizontalLineDivider />

            <ReplyToAssetDiscussionCommentMutation>
              {(replyToAssetDiscussionComment) => (
                <div>
                  <ReplyCommentInputForm
                    viewer={selfie}
                    autoFocus={true}
                    onCancel={() => setIsWritingReply(false)}
                    onSubmit={async (bodyAsPlainText) => {
                      await replyToAssetDiscussionComment({
                        assetId: comment.asset._id,
                        assetType: translateTypenameIntoAssetType(comment.asset.__typename),
                        replyToCommentId: comment._id,
                        replyToCommentShortId: comment.shortId,
                        bodyAsPlainText,
                      });

                      setIsWritingReply(false);
                      setIsShowingReplies(true);
                    }}
                  />
                </div>
              )}
            </ReplyToAssetDiscussionCommentMutation>

            {replies && replies.totalCount > 0 && <HorizontalLineDivider />}
          </>
        )}

        {firstError([
          destroyCommentError,
          editCommentError,
          lockCommentError,
          unlockCommentError,
          pinCommentError,
          unpinCommentError,
        ]) && (
          <div>
            <InlineAssetDiscussionCommentError>
              {translateServerErrorIntoFriendlyMessage(
                firstError([
                  destroyCommentError,
                  editCommentError,
                  lockCommentError,
                  unlockCommentError,
                  pinCommentError,
                  unpinCommentError,
                ]) || new Error("Ih! Tem algo errado...tente novamente mais tarde."),
                intl,
              )}
            </InlineAssetDiscussionCommentError>
          </div>
        )}

        {isShowingReplies && replies && (
          <>
            {!isWritingReply && <HorizontalLineDivider />}

            {replies.nodes.length > 0 && (
              <div className="divide-y divide-slate-100 dark:divide-slate-950">
                {replies.nodes.map((reply) => {
                  return (
                    <div key={reply.shortId} className="py-4 first:pt-0 last:pb-0">
                      <AssetDiscussionCommentReply
                        reply={reply}
                        rootComment={comment}
                        allowToReply={allowToReplyIndividualReplies}
                      />
                    </div>
                  );
                })}
              </div>
            )}

            {replies.pageInfo.hasNextPage && (
              <ViewMoreReplies assetId={comment.asset._id} rootCommentShortId={comment.shortId} repliesPerPage={8} />
            )}
          </>
        )}
      </div>
    </div>
  );
}

function firstError(arrayOfErrors: (Error | null)[]): Error | null {
  return arrayOfErrors.find((error) => error !== null) || null;
}
